// Per-restaurant helpers used by cards, sorts, stats and the picker.
// "Visited" means a logged visit OR a played check game; visit dates are local 'YYYY-MM-DD', check dates are ISO.
import { isoToLocalDate, daysAgoLabel, todayLocal } from './date.js';

const PROFILES = ['doug', 'les'];
export const REVEAL_AFTER_MS = 48 * 3600 * 1000;

/** Average rating (1 dp) and number of distinct visit dates (both of you logging the same night = 1 visit). */
export function restaurantStats(restaurant) {
    const visits = restaurant.visits || [];
    if (visits.length === 0) {
        return { avgRating: restaurant.rating, visitCount: 0, hasVisits: false };
    }
    const avgRating = visits.reduce((sum, v) => sum + v.rating, 0) / visits.length;
    return {
        avgRating: Math.round(avgRating * 10) / 10,
        visitCount: new Set(visits.map((v) => v.date)).size,
        hasVisits: true,
    };
}

/** Average of all visit ratings, rounded to 1 dp, or null. */
export function averageOfVisits(visits) {
    const rated = (visits || []).filter((v) => typeof v.rating === 'number');
    if (!rated.length) return null;
    return Math.round((rated.reduce((s, v) => s + v.rating, 0) / rated.length) * 10) / 10;
}

/** Sorted unique local dates on which you went (visits and check games). */
export function visitedDates(restaurant) {
    const dates = new Set();
    for (const v of restaurant.visits || []) if (v && v.date) dates.add(v.date);
    for (const c of restaurant.checks || []) { const d = c && isoToLocalDate(c.date); if (d) dates.add(d); }
    return [...dates].sort();
}

export function isVisited(restaurant) {
    return visitedDates(restaurant).length > 0;
}

/** Latest visited local date (the max, not the last array element), or null. */
export function lastVisitedDate(restaurant) {
    const d = visitedDates(restaurant);
    return d.length ? d[d.length - 1] : null;
}

export function lastVisitedLabel(restaurant, now = new Date()) {
    const d = lastVisitedDate(restaurant);
    return d ? daysAgoLabel(d, now) : null;
}

export function getLastVisited(restaurant) {
    return lastVisitedLabel(restaurant, new Date());
}

// ---------------------------------------------------------------- blind ratings

/** One row per visit date, newest first: { date, doug: visit|null, les: visit|null }. */
export function visitPairs(visits) {
    const byDate = new Map();
    for (const v of visits || []) {
        if (!v || !v.date) continue;
        const row = byDate.get(v.date) || { date: v.date, doug: null, les: null, other: [] };
        if (PROFILES.includes(v.loggedBy) && !row[v.loggedBy]) row[v.loggedBy] = v;
        else row.other.push(v);
        byDate.set(v.date, row);
    }
    return [...byDate.values()].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

export function partnerOf(profile) {
    return profile === 'doug' ? 'les' : 'doug';
}

/**
 * May `viewer` see the partner's score on this date? Yes when the viewer has rated that date too, when the partner's
 * visit was revealed (Peek), or when it is older than 48 hours (nobody waits forever).
 */
export function canSeeScore(pair, viewer, now = Date.now()) {
    const partner = pair[partnerOf(viewer)];
    if (!partner) return true;
    if (pair[viewer]) return true;
    if (partner.revealed) return true;
    const ts = Date.parse(partner.timestamp || '');
    return Number.isFinite(ts) && now - ts > REVEAL_AFTER_MS;
}

// ---------------------------------------------------------------- want to try

/** 'none' | 'doug' | 'les' | 'both' */
export function wantToTryState(restaurant) {
    const w = restaurant.wantToTry || {};
    if (w.doug && w.les) return 'both';
    if (w.doug) return 'doug';
    if (w.les) return 'les';
    return 'none';
}

export function toggleWantToTry(restaurant, profile, now = new Date()) {
    const w = restaurant.wantToTry || {};
    const next = { ...w, [profile]: !w[profile] };
    if (!next.suggestedBy && next[profile]) { next.suggestedBy = profile; next.suggestedAt = now.toISOString(); }
    return next;
}

export { todayLocal };
